import React, { useEffect, useState } from 'react'
import { Box, MenuItem, Select, Stack, Typography, type SelectChangeEvent } from '@mui/material'
import { getAssignableOrgRoles } from 'pure-js/libs/organizationMembershipHelpers'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'
import { OrgRole } from 'pure-js/types/OrganizationMembership'

import { Button } from '../../../../interaxDesignSystemComponents'
import { DefaultModal, MODAL_Z_INDEX } from '../../../../shared/components/DefaultModal'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { orgRoleTranslationKey } from '../../orgTranslationKeys'
import { TESTIDS } from '../../../../config/testids'
import { LeaderLossWarning } from './LeaderLossWarning'

const ROLE_CHOICES: OrgRole[] = [OrgRole.ADMIN, OrgRole.FACILITATOR, OrgRole.LEADER, OrgRole.GAMER]

interface ChangeRoleModalProps {
  target: OrgMemberRow | null
  onClose: () => void
  onConfirm: (newRole: OrgRole) => void
  isPending: boolean
  isLeaderRoleOffered: boolean
}

export const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  target,
  onClose,
  onConfirm,
  isPending,
  isLeaderRoleOffered
}) => {
  const { t } = useTranslation()
  const [pendingRole, setPendingRole] = useState<OrgRole>(target?.role ?? OrgRole.GAMER)
  const [isDemotionWarningShown, setIsDemotionWarningShown] = useState(false)

  // Sync pending role to whichever member was just opened.
  useEffect(() => {
    if (target) setPendingRole(target.role)
    setIsDemotionWarningShown(false)
  }, [target])

  const memberLabel = target?.displayName || target?.email || target?.userId
  // A leader's own role stays listed, so the role they hold is never missing from their row's choices.
  const isLeaderRoleListed = isLeaderRoleOffered || target?.role === OrgRole.LEADER
  const roleChoices = getAssignableOrgRoles(ROLE_CHOICES, isLeaderRoleListed)
  const isDemotingLeader = target?.role === OrgRole.LEADER && pendingRole !== OrgRole.LEADER
  const isAwaitingDemotionConfirm = isDemotingLeader && isDemotionWarningShown
  const confirmLabel = isAwaitingDemotionConfirm
    ? t('org.members.leaderDemotionConfirm')
    : t('org.members.changeRoleConfirm')

  // A newly chosen role has to be confirmed on its own, so the warning starts over.
  const handleRoleChange = (event: SelectChangeEvent<OrgRole>) => {
    setPendingRole(event.target.value as OrgRole)
    setIsDemotionWarningShown(false)
  }

  // Taking the leader role away is confirmed twice: the first Save only reveals what will be lost.
  const handleSave = () => {
    if (isDemotingLeader && !isDemotionWarningShown) {
      setIsDemotionWarningShown(true)
      return
    }

    onConfirm(pendingRole)
  }

  return (
    <DefaultModal isOpen={target !== null} onClose={onClose} maxWidth="420px">
      <Stack spacing={2.5}>
        <Typography sx={{ fontSize: '20px', fontWeight: 600, color: INTERAX_COLORS.white }}>
          {t('org.members.changeRoleTitle')}
        </Typography>
        {target && (
          <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.white70 }}>
            {target.displayName || target.email}
          </Typography>
        )}
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: INTERAX_COLORS.white70,
              mb: 1,
              letterSpacing: 0.2
            }}
          >
            {t('org.members.invite.roleLabel')}
          </Typography>
          <Select<OrgRole>
            size="small"
            fullWidth
            value={pendingRole}
            onChange={handleRoleChange}
            MenuProps={{
              sx: { zIndex: MODAL_Z_INDEX + 1 },
              PaperProps: {
                sx: {
                  backgroundColor: INTERAX_COLORS.heritageBlue,
                  color: INTERAX_COLORS.white,
                  border: `1px solid ${INTERAX_COLORS.white10}`,
                  '.MuiMenuItem-root': {
                    color: INTERAX_COLORS.white,
                    '&:hover': { backgroundColor: INTERAX_COLORS.white10 },
                    '&.Mui-selected': {
                      backgroundColor: INTERAX_COLORS.sun20,
                      color: INTERAX_COLORS.white,
                      '&:hover': { backgroundColor: INTERAX_COLORS.sun20 }
                    }
                  }
                }
              }
            }}
            sx={{
              color: INTERAX_COLORS.white,
              '.MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.white10 },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: INTERAX_COLORS.white20
              },
              '.MuiSvgIcon-root': { color: INTERAX_COLORS.white70 }
            }}
          >
            {roleChoices.map((r) => (
              <MenuItem key={r} value={r}>
                {t(orgRoleTranslationKey(r))}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {isAwaitingDemotionConfirm && (
          <LeaderLossWarning
            title={t('org.members.leaderDemotionWarningTitle')}
            body={t('org.members.leaderDemotionWarningBody', { name: memberLabel })}
            testId={TESTIDS.ORG_LEADER_DEMOTION_WARNING}
          />
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="secondary" size="small" onClick={onClose}>
            {t('org.members.invite.cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={isPending}
            data-testid={TESTIDS.ORG_CHANGE_ROLE_CONFIRM_BTN}
          >
            {confirmLabel}
          </Button>
        </Stack>
      </Stack>
    </DefaultModal>
  )
}
